/**
 * MicrophoneTest component - Pre-conversation mic check with volume level bars.
 *
 * Design: DENiZEN — matches SessionStart/SessionConfig layout conventions
 * - Lives inside GreenPageLayout's stone card
 * - Centred content with animate-slide-up entrance
 * - WCAG 2.1 AA compliant
 *
 * Shows real-time volume bars so users can see their mic is working
 * before starting the conversation. Offers a skip option for typing.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { useAudioLevel } from '@/hooks/useAudioLevel'

/** Shared microphone SVG icon */
function MicIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
      <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
    </svg>
  )
}

export interface MicrophoneTestProps {
  /** Called when user proceeds to conversation. `micPassed` is true only on success. */
  onContinue: (micPassed: boolean) => void
}

type TestState = 'idle' | 'listening' | 'success' | 'error' | 'unsupported'

/** Max bar height in pixels */
const MAX_BAR_HEIGHT = 64

/** Volume threshold to consider "sound detected" */
const SOUND_THRESHOLD = 0.04

/** How many consecutive frames above threshold before auto-success (~330ms at 15fps) */
const FRAMES_FOR_SUCCESS = 5

export function MicrophoneTest({ onContinue }: MicrophoneTestProps) {
  const { level, bars, isActive, isSupported, error: audioError, start, stop } = useAudioLevel()

  const [testState, setTestState] = useState<TestState>(() =>
    isSupported ? 'idle' : 'unsupported'
  )

  const framesAboveThresholdRef = useRef(0)
  const continueButtonRef = useRef<HTMLButtonElement>(null)

  // Detect sound and transition to success
  useEffect(() => {
    if (testState !== 'listening') return

    if (level > SOUND_THRESHOLD) {
      framesAboveThresholdRef.current++
      if (framesAboveThresholdRef.current >= FRAMES_FOR_SUCCESS) {
        setTestState('success')
      }
    } else {
      framesAboveThresholdRef.current = 0
    }
  }, [level, testState])

  // Handle audio errors
  useEffect(() => {
    if (audioError && testState === 'listening') {
      setTestState('error')
    }
  }, [audioError, testState])

  // Auto-focus continue button on success
  useEffect(() => {
    if (testState !== 'success') return
    // Delay matches animate-fade-in duration so focus shifts after animation
    const timer = setTimeout(() => {
      continueButtonRef.current?.focus()
    }, 300)
    return () => clearTimeout(timer)
  }, [testState])

  const handleTestMicrophone = useCallback(async () => {
    framesAboveThresholdRef.current = 0
    setTestState('listening')
    await start()
  }, [start])

  // Await stop() so the mic stream and AudioContext are fully released before
  // the conversation step acquires a new stream. This fixes the bug where the
  // mic test passes but the chat mic button doesn't work on some browsers.
  const handleContinue = useCallback(async (micPassed: boolean) => {
    await stop()
    onContinue(micPassed)
  }, [stop, onContinue])

  return (
    <div className="px-8 py-6 sm:px-12 animate-slide-up">
      {/* Heading */}
      <header className="mb-6 text-center">
        <h2 className="text-2xl font-display font-bold text-dark-green mb-2">
          Microphone Check
        </h2>
        <p className="text-base font-body text-dark-green/70">
          This chat works best with voice input. Let&apos;s make sure your microphone is working.
        </p>
      </header>

      <hr className="border-dark-green/15 mb-8" />

      {/* Main content area */}
      <div className="max-w-sm mx-auto">

        {/* Idle state */}
        {testState === 'idle' && (
          <div className="text-center animate-fade-in">
            {/* Large mic icon */}
            <div className="mx-auto mb-6 w-20 h-20 rounded-full bg-dark-green/5 border-2 border-dark-green/15 flex items-center justify-center">
              <MicIcon className="w-8 h-8 text-dark-green" />
            </div>

            <button
              type="button"
              onClick={() => void handleTestMicrophone()}
              className="btn-denizen-primary py-3 px-8 mb-4"
            >
              Test my microphone
            </button>
          </div>
        )}

        {/* Listening state */}
        {testState === 'listening' && (
          <div className="text-center animate-fade-in">
            {/* Pulsing mic icon */}
            <div className="mx-auto mb-6 w-20 h-20 rounded-full bg-dark-green flex items-center justify-center animate-[pulse-recording_1s_ease-in-out_infinite]">
              <MicIcon className="w-8 h-8 text-white" />
            </div>

            {/* Volume level bars */}
            <div
              className="flex items-end justify-center gap-1.5 h-16 mb-4"
              role="meter"
              aria-valuenow={Math.round(level * 100)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Microphone input level"
            >
              {bars.map((barLevel, i) => {
                const barHeight = isActive
                  ? Math.max(4, barLevel * MAX_BAR_HEIGHT)
                  : 4
                return (
                  <div
                    key={i}
                    className="w-2 rounded-full bg-mid-green motion-reduce:transition-none"
                    style={{
                      height: `${barHeight}px`,
                      transition: 'height 100ms ease-out',
                    }}
                  />
                )
              })}
            </div>

            {/* Status text */}
            <p
              className="text-sm font-body text-dark-green/70 mb-4"
              role="status"
              aria-live="polite"
            >
              Speak or make a sound...
            </p>
          </div>
        )}

        {/* Success state */}
        {testState === 'success' && (
          <div className="text-center animate-fade-in">
            {/* Green checkmark */}
            <div className="mx-auto mb-6 w-20 h-20 rounded-full bg-mid-green/15 border-2 border-mid-green flex items-center justify-center animate-scale-in">
              <svg className="w-10 h-10 text-mid-green" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <p
              className="text-lg font-display font-semibold text-dark-green mb-2"
              role="status"
              aria-live="polite"
            >
              Your microphone is working!
            </p>
            <p className="text-sm font-body text-dark-green/60 mb-6">
              You&apos;re all set to use voice input during the conversation.
            </p>

            <button
              ref={continueButtonRef}
              type="button"
              onClick={() => void handleContinue(true)}
              className="btn-denizen-primary py-3 px-8"
            >
              Continue
            </button>
          </div>
        )}

        {/* Error state */}
        {testState === 'error' && (
          <div className="animate-fade-in">
            <div
              className="mb-6 p-4 bg-error/10 border border-dark-green/10 rounded-2xl"
              role="alert"
              aria-live="polite"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white rounded-full border border-dark-green/15 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-error" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <p className="text-dark-green text-sm font-body">
                  {audioError || 'Could not access the microphone.'}
                </p>
              </div>
            </div>

            <p className="text-sm font-body text-dark-green/60 text-center mb-6">
              You can still participate using the text input.
            </p>

            <div className="flex flex-col items-center gap-3">
              <button
                type="button"
                onClick={() => void handleTestMicrophone()}
                className="btn-denizen-secondary py-3 px-8"
              >
                Try again
              </button>
              <button
                type="button"
                onClick={() => void handleContinue(false)}
                className="btn-denizen-primary py-3 px-8"
              >
                Continue with typing
              </button>
            </div>
          </div>
        )}

        {/* Unsupported browser state */}
        {testState === 'unsupported' && (
          <div className="text-center animate-fade-in">
            <div className="mx-auto mb-6 w-20 h-20 rounded-full bg-dark-green/5 border-2 border-dark-green/15 flex items-center justify-center">
              <MicIcon className="w-8 h-8 text-dark-green/40" />
            </div>

            <p className="text-sm font-body text-dark-green/70 mb-6">
              Microphone testing is not available in this browser. You can still participate using the text input.
            </p>

            <button
              type="button"
              onClick={() => void handleContinue(false)}
              className="btn-denizen-primary py-3 px-8"
            >
              Continue with typing
            </button>
          </div>
        )}
      </div>

      {/* Skip link — shown in idle and listening states */}
      {(testState === 'idle' || testState === 'listening') && (
        <div className="text-center mt-6">
          <button
            type="button"
            onClick={() => void handleContinue(false)}
            className="text-sm font-body text-dark-green/50 underline underline-offset-2 hover:text-dark-green/70 transition-colors focus:outline-none focus:ring-2 focus:ring-mid-green focus:ring-offset-2 rounded"
          >
            Skip, I&apos;ll type instead
          </button>
        </div>
      )}
    </div>
  )
}
